import { type ReactNode, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button, Text, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { Star16Regular, Star16Filled } from '@fluentui/react-icons';

// Flip to true once the favorites feature ships. Module-level so search
// finds the one switch point and so eslint's no-constant-binary-expression
// doesn't flag an inline `false &&` gate.
const FAVORITES_ENABLED = false;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  iconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusMedium,
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
  },
  name: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameLink: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
    textDecorationLine: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  starButton: {
    color: tokens.colorNeutralForeground3,
    minWidth: 'auto',
    width: '20px',
    height: '20px',
    paddingLeft: 0,
    paddingRight: 0,
  },
  starButtonActive: {
    color: tokens.colorPaletteMarigoldForeground1,
  },
});

export interface EntityNameCellProps {
  /** Icon rendered next to the name. */
  icon: ReactNode;
  /** Display name. */
  name: string;
  /** Optional router link target. If omitted, the name renders as plain text. */
  href?: string;
  /** Click handler fired when the link is followed (telemetry, etc.). */
  onClick?: () => void;
  /** Where the icon sits relative to the name. Defaults to `before`. */
  iconPosition?: 'before' | 'after';
  /** Whether the row is currently a favorite. */
  isFavorite?: boolean;
  /** Toggle handler for the favorite star. Omit to hide the star. */
  onToggleFavorite?: (next: boolean) => void;
}

/**
 * "icon + name + favorite star" cell shared by every list table.
 */
export const EntityNameCell = ({
  icon,
  name,
  href,
  onClick,
  iconPosition = 'before',
  isFavorite,
  onToggleFavorite,
}: EntityNameCellProps) => {
  const styles = useStyles();

  const handleStar = useCallback(() => {
    onToggleFavorite?.(!isFavorite);
  }, [isFavorite, onToggleFavorite]);

  const iconBadge = (
    <span className={styles.iconWrap} aria-hidden="true">
      {icon}
    </span>
  );

  const nameNode = href ? (
    <Link to={href} className={styles.nameLink} onClick={onClick} title={name}>
      {name}
    </Link>
  ) : (
    <Text className={styles.name} title={name}>
      {name}
    </Text>
  );

  return (
    <div className={styles.root}>
      {iconPosition === 'before' && iconBadge}
      {nameNode}
      {iconPosition === 'after' && iconBadge}
      {FAVORITES_ENABLED && onToggleFavorite && (
        <Tooltip content={isFavorite ? 'Remove from favorites' : 'Add to favorites'} relationship="label">
          <Button
            appearance="subtle"
            size="small"
            className={isFavorite ? `${styles.starButton} ${styles.starButtonActive}` : styles.starButton}
            icon={isFavorite ? <Star16Filled /> : <Star16Regular />}
            onClick={handleStar}
            aria-label={isFavorite ? `Unfavorite ${name}` : `Favorite ${name}`}
            aria-pressed={isFavorite}
          />
        </Tooltip>
      )}
    </div>
  );
};
